import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Search } from "lucide-react";
import { toast } from "sonner";
import ShopNav from "../../components/shop/ShopNav";
import { api, mediaUrl } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function Shop() {
  const { t } = useI18n();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort(),
    [products]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchesQ = !q || p.name?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q);
      const matchesC = !category || p.category === category;
      return matchesQ && matchesC;
    });
  }, [products, query, category]);

  return (
    <div className="min-h-screen bg-cream text-navy">
      <ShopNav />
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <div className="overline text-navy/40 mb-3">{t("shop.title")}</div>
          <h1 className="font-editorial text-5xl md:text-6xl tracking-tight max-w-2xl">{t("shop.tagline")}</h1>
        </motion.div>

        {/* Search + category filters */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
            <input data-testid="shop-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("shop.search")}
              className="w-full bg-white border border-navy/10 rounded-full pl-11 pr-4 py-3 outline-none focus:border-violet transition-colors" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="shop-cat-all" onClick={() => setCategory("")}
              className={`text-sm px-4 py-2 rounded-full transition-colors ${category === "" ? "bg-navy text-white" : "border border-navy/15 text-navy/60 hover:border-navy/40"}`}>
              {t("shop.allCategories")}
            </button>
            {categories.map((c) => (
              <button key={c} data-testid={`shop-cat-${c}`} onClick={() => setCategory(c)}
                className={`text-sm px-4 py-2 rounded-full transition-colors ${category === c ? "bg-navy text-white" : "border border-navy/15 text-navy/60 hover:border-navy/40"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-navy/40">…</div>
        ) : filtered.length === 0 ? (
          <div className="text-navy/40 py-16 text-center border border-dashed border-navy/15 rounded-lg" data-testid="shop-no-results">{t("shop.noResults")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
            {filtered.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease, delay: i * 0.05 }}
                data-testid={`shop-product-${p.id}`} className="group">
                <Link to={`/shop/product/${p.id}`} className="block aspect-[4/5] bg-white rounded-lg overflow-hidden border border-navy/10">
                  <img src={mediaUrl(p.cover)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </Link>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="overline text-navy/40 text-[0.6rem]">{p.category}</div>
                    <Link to={`/shop/product/${p.id}`} className="font-medium hover:text-violet transition-colors">{p.name}</Link>
                    <div className="text-sm text-navy/60 mt-1">{money(p.price)} <span className="text-navy/30 text-xs">MXN</span></div>
                  </div>
                  <button data-testid={`shop-add-${p.id}`} onClick={() => { add(p, 1); toast.success(t("shop.added")); }}
                    className="shrink-0 w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center hover:bg-navy-soft transition-colors">
                    <ShoppingBag className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* How it works — purchase-order process */}
      <section className="border-t border-navy/10 bg-white/50" data-testid="shop-process">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="overline text-navy/40 mb-3">{t("shop.process.title")}</div>
          <h2 className="font-editorial text-3xl md:text-4xl tracking-tight max-w-2xl">{t("shop.process.heading")}</h2>
          <p className="mt-3 text-navy/55 max-w-2xl">{t("shop.process.intro")}</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(Array.isArray(t("shop.process.steps")) ? t("shop.process.steps") : []).map((s, idx) => (
              <div key={idx} className="relative">
                <div className="text-5xl font-editorial text-violet/15">{idx + 1}</div>
                <div className="font-medium mt-1">{s.title}</div>
                <p className="text-sm text-navy/55 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xs text-navy/40 max-w-2xl">{t("shop.process.footnote")}</p>
        </div>
      </section>
    </div>
  );
}
